import { useCallback, useEffect, useMemo } from 'react';
import {
  createConfigurableAjv,
  validateData,
  type FormError,
} from '@web-config-app/validation';
import {
  ConfigErrorType,
  type AnnotatedJsonSchema,
  type EntityDetailError,
} from '@web-config-app/core';
import { useIntl } from '@leagueplatform/locales';
import { useConfigUserFeedback } from '../../state/config-user-feedback-state/config-user-feedback-state';
import { useConfigApp } from '../use-config-app/use-config-app';

type FormatMessage = ReturnType<typeof useIntl>['formatMessage'];

const formatFormError = (
  error: FormError,
  formatMessage: FormatMessage,
): EntityDetailError => {
  if (error.message) {
    const detail = error.propertyName
      ? `${formatMessage({ id: error.propertyName })} ${error.message}`
      : error.message;
    return { detail };
  }
  return { detail: `${error.keyword} error: ${error.schemaPath}` };
};

interface UseSchemaFormValidationProps {
  schema: AnnotatedJsonSchema;
  data: Record<string, unknown>;
}

/**
 * Client-side AJV validation for schema-driven admin forms.
 *
 * This is a lightweight alternative to `useEntityFormError` (entity-form package)
 * for pages that don't live inside the entity details context with the complex tree node map.
 * TODO:: A future consolidation could extract a shared validation core from both hooks.
 * @returns validate – returns `true` when the form is valid
 */
export const useSchemaFormValidation = ({
  schema,
  data,
}: UseSchemaFormValidationProps) => {
  const { formatMessage } = useIntl();
  const { frontendValidation } = useConfigApp();

  const {
    setConfigError,
    setShowErrorBanner,
    setShowFormValidation,
    showFormValidation,
    resetConfigUserFeedbackStore,
  } = useConfigUserFeedback((state) => ({
    setConfigError: state.setConfigError,
    setShowErrorBanner: state.setShowErrorBanner,
    setShowFormValidation: state.setShowFormValidation,
    showFormValidation: state.showFormValidation,
    resetConfigUserFeedbackStore: state.resetConfigUserFeedbackStore,
  }));

  const ajv = useMemo(
    () => createConfigurableAjv(frontendValidation?.uriSchemePolicy?.allowed),
    [frontendValidation?.uriSchemePolicy?.allowed],
  );

  const validate = useCallback((): boolean => {
    const { formErrors } = validateData({
      schema,
      data,
      ajv,
    });

    if (formErrors.length === 0) {
      resetConfigUserFeedbackStore();
      return true;
    }

    const errors: EntityDetailError[] = formErrors.map((error) =>
      formatFormError(error, formatMessage),
    );

    setConfigError({
      type: ConfigErrorType.FormValidationError,
      errors,
    });
    setShowErrorBanner(true);
    setShowFormValidation(true);

    return false;
  }, [
    schema,
    data,
    ajv,
    formatMessage,
    setConfigError,
    setShowErrorBanner,
    setShowFormValidation,
    resetConfigUserFeedbackStore,
  ]);

  /**
   * Re-validate on data changes while validation mode is active,
   * mirroring the live-feedback pattern from useEntityFormError.
   */
  useEffect(() => {
    if (showFormValidation) {
      validate();
    }
  }, [data, showFormValidation, validate]);

  return { validate };
};
